import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Coffee, CheckCircle2, FileText } from "lucide-react"; // eslint-disable-line no-unused-vars
import { Button } from "@/components/ui/button";
import { api, apiError, API } from "@/lib/api";
import { fmtEur, STATUS_LABELS } from "@/lib/quote";

export default function ConfirmQuote() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setQ((await api.get(`/quotes/resume/${token}`)).data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setSending(true);
    try {
      const r = await api.post(`/quotes/accept/${token}`);
      setQ(r.data);
      toast.success("Preventivo accettato! Il Bliss Café è stato avvisato.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  if (error) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-6 text-center font-semibold">{error}</div>;
  if (!q) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center text-sm text-[#8a7a6d]">Caricamento…</div>;

  const approved = ["approvato", "acconto_ricevuto", "confermato", "completato"].includes(q.status);
  const accepted = !!q.client_accepted_at;

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-16">
      <header className="bg-[#2C1A11] text-[#FAF7F2] py-5 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <img src="/logo.png" alt="Bliss Café" className="h-12 w-auto" />
          <div className="text-xs text-[#FAF7F2]/60 self-end pb-1">Conferma preventivo</div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 mt-6 space-y-4">
        <div className="bg-white rounded-2xl border p-6">
          <h1 className="font-display text-2xl font-bold text-[#2C1A11]">Preventivo n. {q.number}</h1>
          <p className="text-sm text-[#8a7a6d] mt-1">
            {q.event.type}{q.event.type_custom ? ` — ${q.event.type_custom}` : ""} · {q.event.date} · {q.event.time_start}–{q.event.time_end}
          </p>
          <div className="divide-y mt-4">
            {q.items.map((it, i) => (
              <div key={`${it.product_id}-${i}`} className="py-2 flex justify-between text-sm">
                <span>{it.name} <span className="text-[#8a7a6d]">x{it.qty}</span></span>
                <span className="font-mono-num font-semibold">{fmtEur(it.total ?? it.price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center mt-4 pt-4 border-t">
            <span className="text-sm text-[#8a7a6d]">Totale (IVA inclusa)</span>
            <span data-testid="confirm-total" className="font-mono-num text-2xl font-bold text-[#C85A32]">{fmtEur(q.totals.total)}</span>
          </div>
        </div>

        {!approved ? (
          <div data-testid="confirm-not-ready" className="bg-white rounded-2xl border p-6 text-center text-sm text-[#8a7a6d]">
            Il preventivo è in stato <b>{STATUS_LABELS[q.status]}</b>: potrai confermarlo qui non appena sarà approvato dal Bliss Café.
          </div>
        ) : accepted ? (
          <div data-testid="confirm-accepted" className="bg-emerald-50 border border-emerald-300 rounded-2xl p-6 text-center space-y-2">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <div className="font-bold text-emerald-800">Preventivo accettato!</div>
            <p className="text-sm text-emerald-700">Per riservare la data versa l'acconto del 30% ({fmtEur(q.totals.total * 0.3)}) con bonifico (IBAN nel PDF) o POS in sede.</p>
          </div>
        ) : (
          <>
            <Button data-testid="accept-quote-button" disabled={sending} onClick={accept}
              className="w-full h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-base font-bold">
              <CheckCircle2 className="w-5 h-5 mr-2" /> Accetto il preventivo
            </Button>
            <p className="text-xs text-center text-[#8a7a6d]">Toccando "Accetto" confermi il preventivo: riceverai le istruzioni per l'acconto del 30%.</p>
          </>
        )}

        {approved && (
          <a data-testid="confirm-pdf-link" href={`${API}/quotes/${q.id}/pdf`} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-2 text-sm text-[#8a7a6d] hover:text-[#C85A32] py-2">
            <FileText className="w-4 h-4" /> Scarica il PDF del preventivo
          </a>
        )}
      </main>
    </div>
  );
}
